import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { Icons } from '../components/Icons';

function AlertSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    lowStockThreshold: 5,
    longTermUnsoldDays: 90,
    reservationExpiryWarning: 3,
    consignmentSettlementDays: 60,
    enableDashboardAlerts: true,
    enableSlackAlerts: true,
    enableEmailAlerts: false,
  });

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const settingsRef = doc(db, 'settings', 'alerts');
      const docSnap = await getDoc(settingsRef);

      if (docSnap.exists()) {
        setSettings({ ...settings, ...docSnap.data() });
      }
    } catch (error) {
      console.error('알림 설정 불러오기 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const settingsRef = doc(db, 'settings', 'alerts');
      await setDoc(settingsRef, {
        ...settings,
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });

      alert('✅ 알림 설정이 저장되었습니다.');
    } catch (error) {
      console.error('알림 설정 저장 오류:', error);
      alert('알림 설정 저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold text-gray-800">알림 설정</h2>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-brand-600 hover:bg-brand-700 text-white px-6 py-2 rounded-lg transition disabled:opacity-50 flex items-center"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              저장 중...
            </>
          ) : (
            <>
              <Icons.Check className="w-5 h-5 mr-2" />
              설정 저장
            </>
          )}
        </button>
      </div>

      <div className="space-y-6">
        {/* 저재고 알림 */}
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <div className="flex items-center mb-4">
            <Icons.Box className="w-6 h-6 text-brand-600 mr-3" />
            <h3 className="text-xl font-semibold text-gray-800">저재고 알림</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            재고 수량이 설정한 값 미만일 때 알림을 받습니다.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                최소 재고 수량
              </label>
              <input
                type="number"
                min="0"
                value={settings.lowStockThreshold}
                onChange={(e) => handleChange('lowStockThreshold', parseInt(e.target.value) || 0)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                재고가 이 수량 미만이면 알림이 발송됩니다
              </p>
            </div>
          </div>
        </div>

        {/* 장기 미판매 알림 */}
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <div className="flex items-center mb-4">
            <Icons.Calendar className="w-6 h-6 text-brand-600 mr-3" />
            <h3 className="text-xl font-semibold text-gray-800">장기 미판매 재고 알림</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            일정 기간 동안 판매되지 않은 재고에 대해 알림을 받습니다.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                미판매 기간 (일)
              </label>
              <select
                value={settings.longTermUnsoldDays}
                onChange={(e) => handleChange('longTermUnsoldDays', parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value={30}>30일 (1개월)</option>
                <option value={60}>60일 (2개월)</option>
                <option value={90}>90일 (3개월)</option>
                <option value={180}>180일 (6개월)</option>
                <option value={365}>365일 (1년)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                이 기간 동안 판매되지 않으면 알림이 발송됩니다
              </p>
            </div>
          </div>
        </div>

        {/* 예약 만료 알림 */}
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <div className="flex items-center mb-4">
            <Icons.Clock className="w-6 h-6 text-brand-600 mr-3" />
            <h3 className="text-xl font-semibold text-gray-800">예약 만료 알림</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            예약 만료일 전에 미리 알림을 받습니다.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                사전 알림 (일)
              </label>
              <select
                value={settings.reservationExpiryWarning}
                onChange={(e) => handleChange('reservationExpiryWarning', parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value={1}>1일 전</option>
                <option value={3}>3일 전</option>
                <option value={7}>7일 전</option>
                <option value={14}>14일 전</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                예약 만료일 며칠 전에 알림을 받을지 설정합니다
              </p>
            </div>
          </div>
        </div>

        {/* 위탁 정산 기한 알림 */}
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <div className="flex items-center mb-4">
            <Icons.Users className="w-6 h-6 text-brand-600 mr-3" />
            <h3 className="text-xl font-semibold text-gray-800">위탁 정산 기한 알림</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            위탁 제품의 정산 기한이 다가오면 알림을 받습니다.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                정산 주기 (일)
              </label>
              <select
                value={settings.consignmentSettlementDays}
                onChange={(e) => handleChange('consignmentSettlementDays', parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value={30}>30일 (월 정산)</option>
                <option value={60}>60일 (2개월)</option>
                <option value={90}>90일 (분기 정산)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                위탁 입고일로부터 이 기간이 지나면 정산 알림이 발송됩니다
              </p>
            </div>
          </div>
        </div>

        {/* 알림 수신 방법 */}
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <div className="flex items-center mb-4">
            <Icons.Bell className="w-6 h-6 text-brand-600 mr-3" />
            <h3 className="text-xl font-semibold text-gray-800">알림 수신 방법</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            알림을 받을 방법을 선택하세요.
          </p>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.enableDashboardAlerts}
                onChange={(e) => handleChange('enableDashboardAlerts', e.target.checked)}
                className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
              />
              <span className="ml-3 text-gray-700">대시보드 알림</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.enableSlackAlerts}
                onChange={(e) => handleChange('enableSlackAlerts', e.target.checked)}
                className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
              />
              <span className="ml-3 text-gray-700">Slack 알림</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.enableEmailAlerts}
                onChange={(e) => handleChange('enableEmailAlerts', e.target.checked)}
                className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
              />
              <span className="ml-3 text-gray-700">이메일 알림 (향후 지원 예정)</span>
            </label>
          </div>
        </div>
      </div>

      {/* 저장 버튼 (하단) */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-brand-600 hover:bg-brand-700 text-white px-8 py-3 rounded-lg transition disabled:opacity-50 flex items-center text-lg font-medium"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              저장 중...
            </>
          ) : (
            <>
              <Icons.Check className="w-5 h-5 mr-2" />
              설정 저장
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default AlertSettings;
